import React, { useState } from "react";
import { collection, addDoc } from "firebase/firestore";
import { db } from "../firebase";
import type { AnnualPlan, Period, Theme } from "../types/annualPlan";
import { useAuthContext } from "../contexts/AuthContext";
import { Button, Box, Typography, Card, CardContent, MenuItem, TextField, Grid, IconButton } from "@mui/material";
import AddIcon from "@mui/icons-material/AddCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";

const THEME_LABELS = {
  tactique: ["Jeu dans les couloirs", "Conservation", "Pressing haut"],
  technique: ["Passe courte", "Finition", "Contrôle orienté"],
  athletique: ["Vitesse", "Coordination", "Endurance"],
  mental: ["Concentration", "Motivation", "Confiance"],
};

const emptyTheme = (): Theme => ({ weeks: 1, sessions: 1, label: "" });

const emptyPeriod = (): Period => ({
  title: "",
  comments: "",
  themes: {
    tactique: emptyTheme(),
    technique: emptyTheme(),
    athletique: emptyTheme(),
    mental: emptyTheme(),
  },
});

export default function Planification() {
  const { user } = useAuthContext();
  const currentUser = user;
  const [title, setTitle] = useState("");
  const [season, setSeason] = useState("");
  const [club, setClub] = useState("");
  const [category, setCategory] = useState("");
  const [comments, setComments] = useState("");
  const [periods, setPeriods] = useState<Period[]>([emptyPeriod()]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handlePeriodChange = (idx: number, field: keyof Period, value: any) => {
    const updated = [...periods];
    updated[idx] = { ...updated[idx], [field]: value };
    setPeriods(updated);
  };

  const handleThemeChange = (idx: number, theme: keyof Period["themes"], field: keyof Theme, value: any) => {
    const updated = [...periods];
    updated[idx].themes = { ...updated[idx].themes, [theme]: { ...updated[idx].themes[theme], [field]: value } };
    setPeriods(updated);
  };

  const addPeriod = () => setPeriods([...periods, emptyPeriod()]);
  const removePeriod = (idx: number) => setPeriods(periods.filter((_, i) => i !== idx));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;
    setLoading(true);
    setSuccess(false);
    const plan: Omit<AnnualPlan, "id"> = {
      userId: currentUser.uid,
      title,
      season,
      club,
      category,
      comments,
      periods,
      createdAt: new Date().toISOString(),
    };
    try {
      await addDoc(collection(db, "annualPlans"), plan);
      setSuccess(true);
    } catch (err) {
      alert("Erreur lors de l'enregistrement : " + err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", mt: 4, mb: 6 }}>
      <Typography variant="h4" fontWeight={700} color="primary" gutterBottom>
        Nouvelle planification annuelle
      </Typography>
      <TextField
        label="Titre du plan"
        value={title}
        onChange={e => setTitle(e.target.value)}
        fullWidth
        required
        sx={{ mb: 2 }}
      />
      <Card sx={{ borderRadius: "16px", backgroundColor: "background.paper", boxShadow: "0 2px 10px rgba(0,0,0,0.06)", border: "1px solid rgba(0,0,0,0.08)" }}>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid gridColumn={{ xs: 'span 12', sm: 'span 6' }}>
                <TextField label="Saison" value={season} onChange={e => setSeason(e.target.value)} fullWidth required />
              </Grid>
              <Grid gridColumn={{ xs: 'span 12', sm: 'span 6' }}>
                <TextField label="Club" value={club} onChange={e => setClub(e.target.value)} fullWidth required />
              </Grid>
              <Grid gridColumn={{ xs: 'span 12', sm: 'span 6' }}>
                <TextField label="Catégorie" value={category} onChange={e => setCategory(e.target.value)} fullWidth required />
              </Grid>
              <Grid gridColumn={{ xs: 'span 12' }}>
                <TextField label="Commentaires généraux" value={comments} onChange={e => setComments(e.target.value)} fullWidth multiline minRows={2} />
              </Grid>
            </Grid>
            <Box mt={4}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Périodes
              </Typography>
              {periods.map((period, idx) => (
  <Card
    key={idx}
    sx={{
      mb: 5,
      borderRadius: 4,
      backgroundColor: "background.paper",
      color: "text.primary",
      boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
      border: "1px solid rgba(0,0,0,0.08)",
      position: 'relative',
      transition: 'box-shadow 0.3s, border 0.3s',
      '&:hover': {
        boxShadow: '0 8px 20px rgba(0,0,0,0.10)',
        borderColor: 'rgba(25,118,210,0.30)',
      },
    }}
  >
    <CardContent>
      <Grid container spacing={3} alignItems="flex-end">
        <Grid gridColumn={{ xs: 'span 12', sm: 'span 7' }}>
          <TextField
            label="Titre de la période"
            value={period.title}
            onChange={e => handlePeriodChange(idx, "title", e.target.value)}
            fullWidth
            required
            sx={{
              input: { fontWeight: 700, fontSize: '1.2rem', letterSpacing: 0.2 },
              '.MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: '#fff',
                '& fieldset': { borderColor: 'rgba(0,0,0,0.18)' },
                '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.30)' },
                '&.Mui-focused fieldset': { borderColor: 'rgba(25,118,210,0.70)' },
              }
            }}
            placeholder="Ex : Préparation estivale"
          />
        </Grid>
        <Grid gridColumn={{ xs: 'span 12', sm: 'span 5' }}>
          <TextField
            label="Commentaires (optionnel)"
            value={period.comments}
            onChange={e => handlePeriodChange(idx, "comments", e.target.value)}
            fullWidth
            sx={{
              '.MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: '#fff',
                '& fieldset': { borderColor: 'rgba(0,0,0,0.18)' },
                '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.30)' },
                '&.Mui-focused fieldset': { borderColor: 'rgba(25,118,210,0.70)' },
              }
            }}
            placeholder="Ajoutez un commentaire sur la période"
          />
        </Grid>
        {([
          { key: "tactique", icon: <span style={{fontSize:'1.5rem'}}>­ƒÄ»</span>, color: '#42a5f5' },
          { key: "technique", icon: <span style={{fontSize:'1.5rem'}}>ÔÜ¢</span>, color: '#7e57c2' },
          { key: "athletique", icon: <span style={{fontSize:'1.5rem'}}>­ƒÅâÔÇìÔÖé´©Å</span>, color: '#26c6da' },
          { key: "mental", icon: <span style={{fontSize:'1.5rem'}}>­ƒºá</span>, color: '#ffb300' }
        ] as const).map(({ key: themeKey, icon, color }) => (
          <Grid gridColumn={{ xs: 'span 12', sm: 'span 6', md: 'span 3' }} key={themeKey}>
            <Box display="flex" alignItems="center" mb={1}>
              <span style={{ marginRight: 8, fontSize: '1.5rem', color }}>{icon}</span>
              <Typography fontWeight={700} sx={{ color, fontSize: '1.08rem' }}>{themeKey.charAt(0).toUpperCase() + themeKey.slice(1)}</Typography>
            </Box>
            <TextField
              select
              label="Thème"
              value={period.themes[themeKey].label}
              onChange={e => handleThemeChange(idx, themeKey, "label", e.target.value)}
              fullWidth
              sx={{
                mb: 1,
                label: { color },
                '.MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#fff',
                  '& fieldset': { borderColor: 'rgba(0,0,0,0.18)' },
                  '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.30)' },
                  '&.Mui-focused fieldset': { borderColor: 'rgba(25,118,210,0.70)' },
                }
              }}
              InputLabelProps={{ style: { color } }}
              placeholder="Choisir..."
            >
              <MenuItem value="">Choisir...</MenuItem>
              {THEME_LABELS[themeKey].map(option => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Semaines"
              value={period.themes[themeKey].weeks}
              onChange={e => handleThemeChange(idx, themeKey, "weeks", Number(e.target.value))}
              fullWidth
              sx={{
                mb: 1,
                label: { color },
                '.MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#fff',
                  '& fieldset': { borderColor: 'rgba(0,0,0,0.18)' },
                  '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.30)' },
                  '&.Mui-focused fieldset': { borderColor: 'rgba(25,118,210,0.70)' },
                }
              }}
              InputLabelProps={{ style: { color } }}
            >
              {[...Array(8)].map((_, i) => (
                <MenuItem key={i+1} value={i+1}>{i+1} semaine{i>0?'s':''}</MenuItem>
              ))}
            </TextField>
            <TextField
              type="number"
              label="Séances"
              value={period.themes[themeKey].sessions}
              onChange={e => handleThemeChange(idx, themeKey, "sessions", Number(e.target.value))}
              fullWidth
              inputProps={{ min: 0 }}
              sx={{
                label: { color },
                '.MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#fff',
                  '& fieldset': { borderColor: 'rgba(0,0,0,0.18)' },
                  '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.30)' },
                  '&.Mui-focused fieldset': { borderColor: 'rgba(25,118,210,0.70)' },
                }
              }}
              InputLabelProps={{ style: { color } }}
              placeholder="Nombre de séances"
            />
          </Grid>
        ))}
      </Grid>
      <Box mt={3} display="flex" justifyContent="flex-end">
        {periods.length > 1 && (
          <IconButton color="error" onClick={() => removePeriod(idx)} sx={{ backgroundColor: 'rgba(244, 67, 54, 0.08)', borderRadius: 2, ml: 1, transition: 'all 0.2s', '&:hover': { backgroundColor: 'rgba(244, 67, 54, 0.14)' } }}>
            <DeleteIcon />
          </IconButton>
        )}
      </Box>
    </CardContent>
  </Card>
))}

              <Button
  variant="contained"
  startIcon={<AddIcon />}
  onClick={addPeriod}
  sx={{
    mt: 2,
    mb: 2,
    py: 1.4,
    px: 4,
    borderRadius: 3,
    fontWeight: 800,
    fontSize: '1.08rem',
    backgroundColor: 'primary.main',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    textTransform: 'none',
    transition: 'all 0.2s',
    '&:hover': {
      backgroundColor: 'primary.dark',
      boxShadow: '0 3px 10px rgba(0,0,0,0.16)',
    }
  }}
>
  Ajouter une période
</Button>
            </Box>
            <Box mt={4} display="flex" justifyContent="flex-end">
              <Button type="submit" variant="contained" color="primary" disabled={loading} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
                {loading ? "Enregistrement..." : "Enregistrer le plan"}
              </Button>
            </Box>
            {success && <Typography color="success.main" sx={{ mt: 2 }}>Planification enregistr├®e avec succ├¿s !</Typography>}
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
